import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOdExitInterviewsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function OffboardingExitInterviewsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, objectSchema, create, markSent, cancel } = useOdExitInterviewsApi(queryParams);

  const doCreate = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [create]);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.status === 'pending') {
      actions.push({
        type: 'edit',
        label: t('offboardingExitInterview.action.markSent'),
        onClick: () => setConfirm({
          message: t('offboardingExitInterview.confirm.markSent'),
          onYes: () => markSent(row.id),
        }),
      });
    }
    if (row.status === 'pending' || row.status === 'sent') {
      actions.push({
        type: 'delete',
        label: t('offboardingExitInterview.action.cancel'),
        onClick: () => setConfirm({
          message: t('offboardingExitInterview.confirm.cancel'),
          onYes: () => cancel(row.id),
        }),
      });
    }
    return actions;
  }, [t, writable, markSent, cancel]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('offboardingExitInterview.new')}
        schema={objectSchema}
        initialValues={{ method: 'digital_form' }}
        onSave={doCreate}
      />

      {useMemo(() => (
        <DataTable
          title={t('offboardingExitInterview.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, writable, handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
